import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, Menu, X, Shield } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import ThemeToggle from "./ThemeToggle";

const Navbar = () => {
  const { count, setIsOpen } = useCart();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-1 group">
          <span className="font-serif text-2xl md:text-3xl tracking-[0.2em] font-light text-[#F5F5F5] group-hover:text-gold transition-colors duration-500">
            VINCEN
          </span>
          <span className="lux-star w-1.5 h-1.5 bg-gold rotate-45 translate-y-[-2px]" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <Link
            to="/"
            data-testid="nav-home-link"
            className="lux-link text-xs tracking-[0.3em] uppercase text-zinc-400 hover:text-gold transition-colors duration-300"
          >
            The House
          </Link>
          <Link
            to="/shop"
            data-testid="nav-shop-link"
            className="lux-link text-xs tracking-[0.3em] uppercase text-zinc-400 hover:text-gold transition-colors duration-300"
          >
            Collections
          </Link>
          {user && user.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="lux-link text-xs tracking-[0.3em] uppercase text-zinc-400 hover:text-gold transition-colors duration-300 flex items-center gap-2"
            >
              <Shield size={12} /> Atelier
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-5 md:gap-7">
          <ThemeToggle />
          <button
            data-testid="nav-account-button"
            onClick={() => navigate(user ? "/account" : "/auth")}
            className="text-zinc-400 hover:text-gold transition-colors duration-300"
            aria-label="Account"
          >
            <User size={19} strokeWidth={1.5} />
          </button>
          <button
            data-testid="nav-cart-button"
            onClick={() => setIsOpen(true)}
            className="relative text-zinc-400 hover:text-gold transition-colors duration-300"
            aria-label="Cart"
          >
            <ShoppingBag size={19} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-2 -right-2 w-4 h-4 bg-gold text-black text-[10px] font-semibold flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-zinc-400 hover:text-gold transition-colors"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-black/80"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <Link to="/" data-testid="nav-mobile-home-link" onClick={() => setMobileOpen(false)} className="text-sm tracking-[0.3em] uppercase text-zinc-300">
                The House
              </Link>
              <Link to="/shop" data-testid="nav-mobile-shop-link" onClick={() => setMobileOpen(false)} className="text-sm tracking-[0.3em] uppercase text-zinc-300">
                Collections
              </Link>
              {user && user.role === "admin" && (
                <Link to="/admin" data-testid="nav-mobile-admin-link" onClick={() => setMobileOpen(false)} className="text-sm tracking-[0.3em] uppercase text-zinc-300">
                  Atelier
                </Link>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
